import React, { useContext } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { AuthProvider, AuthContext } from './auth/ContextAuthentication';
import { ErrorLoadingProvider } from './auth/ErrorLoadingContext';
import { ADMIN_CAPABILITIES } from './auth/adminCapabilities';
import Login from './pages/Login';
import UpdatePassword from './pages/UpdatePassword';
import GroundProfile from './pages/GroundProfile';
import PlayersProfile from './pages/PlayersProfile';
import MatchProfile from './pages/MatchProfile';
import Dashboard from './pages/Dashboard';
import SideNav from './component/SideNav';
import TopNav from './component/TopNav';
import PaymentVerfication from './pages/PaymentVerfication';
import Receipts from './pages/Receipts';
import CreatePromotion from './pages/CreatePromotion';
import ViewAllPromotions from './pages/ViewAllPromotions';
import Bookings from './pages/Bookings';
import SportsBookings from './pages/SportsBookings';
import AcademiesProfile from './pages/AcademiesProfile';
import AcademyRegistrations from './pages/AcademyRegistrations';
import GroundSettlement from './pages/GroundSettlement';
import Teams from './pages/Teams';
import AdminManagement from './pages/AdminManagement';
import PremiumOperations from './pages/PremiumOperations';

const App = () => (
  <AuthProvider>
    <ErrorLoadingProvider>
      <Router>
        <AppContent />
      </Router>
    </ErrorLoadingProvider>
  </AuthProvider>
);

const AccessDenied = () => (
  <div className="flex min-h-screen items-center justify-center bg-gray-100 p-6">
    <div className="rounded bg-white p-6 text-center shadow">
      <h1 className="text-xl font-semibold">Access denied</h1>
      <p className="mt-2 text-gray-600">Your verified administrator session does not include this capability.</p>
    </div>
  </div>
);

const AppContent = () => {
  const { authenticated, initializing, hasCapability, open } = useContext(AuthContext);

  if (initializing) {
    return <div className="flex min-h-screen items-center justify-center bg-gray-100">Verifying administrator session…</div>;
  }

  const protectedElement = (element, requiredCapabilities) => {
    if (!authenticated) return <Login />;
    const requirements = Array.isArray(requiredCapabilities)
      ? requiredCapabilities
      : requiredCapabilities
        ? [requiredCapabilities]
        : [];
    if (requirements.length && !requirements.some(hasCapability)) return <AccessDenied />;
    return element;
  };

  return (
    <div className="flex min-h-screen bg-gray-100">
      {authenticated && <SideNav />}
      <div className="flex-1">
        {authenticated && <TopNav />}
        <main
          className={`flex-1 transition-all duration-300 ${authenticated ? (open ? 'ml-20 p-4' : 'ml-60 p-4') : ''}`}
          style={authenticated ? {width: open ? 'calc(100vw - 5rem)' : 'calc(100vw - 15rem)'} : {}}>
          <Routes>
            <Route path="/" element={protectedElement(<Dashboard />)} />
            <Route path="/login" element={<Login />} />
            <Route path="/administrators" element={protectedElement(<AdminManagement />, ADMIN_CAPABILITIES.PROVISION)} />
            <Route path="/playersprofile" element={protectedElement(<PlayersProfile />, ADMIN_CAPABILITIES.USERS_MANAGE)} />
            <Route path="/groundprofile" element={protectedElement(<GroundProfile />, ADMIN_CAPABILITIES.GROUNDS_MANAGE)} />
            <Route path="/groundsettlement" element={protectedElement(<GroundSettlement />, ADMIN_CAPABILITIES.PAYMENTS_READ)} />
            <Route path="/bookings" element={protectedElement(<Bookings />, ADMIN_CAPABILITIES.BOOKINGS_MANAGE)} />
            <Route path="/updatepassword" element={protectedElement(<UpdatePassword />)} />
            <Route path="/matchprofile" element={protectedElement(<MatchProfile />, ADMIN_CAPABILITIES.MATCHES_MANAGE)} />
            <Route path="/receipts" element={protectedElement(<Receipts />, ADMIN_CAPABILITIES.PAYMENTS_READ)} />
            <Route path="/sportsbookings" element={protectedElement(<SportsBookings />, [ADMIN_CAPABILITIES.SPORTS_MANAGE, ADMIN_CAPABILITIES.EXPORTS_READ])} />
            <Route path="/academies" element={protectedElement(<AcademiesProfile />, ADMIN_CAPABILITIES.ACADEMIES_MANAGE)} />
            <Route path="/paymentverification" element={protectedElement(<PaymentVerfication />, ADMIN_CAPABILITIES.PAYMENTS_MANAGE)} />
            <Route path="/teams" element={protectedElement(<Teams />)} />
            <Route path="/premium" element={protectedElement(<PremiumOperations />, [ADMIN_CAPABILITIES.PREMIUM_READ, ADMIN_CAPABILITIES.PREMIUM_REPORTS_READ])} />
            <Route path="/promotions/create" element={protectedElement(<CreatePromotion />, ADMIN_CAPABILITIES.PROMOTIONS_MANAGE)} />
            <Route path="/promotions/view-all" element={protectedElement(<ViewAllPromotions />, ADMIN_CAPABILITIES.PROMOTIONS_MANAGE)} />
            <Route path="/academy-registrations" element={protectedElement(<AcademyRegistrations />, ADMIN_CAPABILITIES.ACADEMIES_MANAGE)} />
          </Routes>
        </main>
      </div>
    </div>
  );
};

export default App;
